// types
import { createAsyncThunk, createSlice, SerializedError } from '@reduxjs/toolkit';
import http from 'apis/http';
import { Agenda, User } from 'apis/types';
import axios, { AxiosResponse } from 'axios';
import config from 'config';

export type State = Partial<Pick<User, 'id' | 'accessToken' | 'refreshToken' | 'agendaId' | 'agendaUuid'>> & {
    loginError?: SerializedError;
    registerError?: SerializedError;
};

export interface SignupState {
    user: Partial<User>;
    password: string;
    agenda: Partial<Agenda>;
    prefix: string;
    referralCode?: string;
}

export interface LoginState {
    username: string;
    password: string;
}

// initial state
const initialState: State = {
    id: sessionStorage.getItem('id') ? parseInt(sessionStorage.getItem('id') as string) : undefined,
    accessToken: sessionStorage.getItem('accessToken') || undefined,
    refreshToken: sessionStorage.getItem('refreshToken') || undefined,
    agendaId: sessionStorage.getItem('agendaId') ? parseInt(sessionStorage.getItem('agendaId') as string) : undefined,
    agendaUuid: sessionStorage.getItem('agendaUuid') ? (sessionStorage.getItem('agendaUuid') as string) : undefined
};

// ==============================|| SLICE - AUTH ||============================== //

export const signup = createAsyncThunk<AxiosResponse, SignupState>(
    'auth/signup',
    async ({ user, password, agenda, prefix, referralCode }, { rejectWithValue }) => {
        const url = `${config.serverUrl}/api/auth/user/signup`;
        console.log('AUTH - SIGNUP URL', url);
        console.log('AUTH - SIGNUP PARAMS', { user, password, agenda, prefix, referralCode });

        try {
            const response = await http.post(
                url,
                { user, password, agenda, prefix, referralCode },
                {
                    validateStatus: (status: number) => status >= 200 && status < 300
                }
            );
            return response;
        } catch (error) {
            if (axios.isAxiosError(error)) return rejectWithValue(error.response?.data ?? error);
            throw error;
        }
    }
);

export const login = createAsyncThunk<User, LoginState>('auth/login', async ({ username, password }, { rejectWithValue }) => {
    const url = `${config.serverUrl}/api/auth/signin`;
    console.log('AUTH - LOGIN URL', url);

    try {
        const { data } = await http.post<User>(
            url,
            {
                username,
                password
            },
            {
                validateStatus: (status: number) => status >= 200 && status < 300
            }
        );
        console.log('AUTH - LOGIN SUCCEDED', data);
        return data;
    } catch (error) {
        console.log('AUTH - LOGIN FAILED', error);
        if (axios.isAxiosError(error)) return rejectWithValue(error.response?.data ?? error);
        throw error;
    }
});

const auth = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        logout(state) {
            delete state.id;
            delete state.accessToken;
            delete state.refreshToken;
            delete state.agendaId;
            delete state.agendaUuid;
            sessionStorage.removeItem('id');
            sessionStorage.removeItem('accessToken');
            sessionStorage.removeItem('refreshToken');
            sessionStorage.removeItem('agendaId');
            sessionStorage.removeItem('agendaUuid');
        }
    },
    extraReducers: (builder) => {
        builder.addCase(signup.fulfilled, (state, action) => {
            console.log(`AUTH - SIGNED UP AS ${action?.meta?.arg?.user?.username || ''}`);
            state.registerError = undefined;
        });
        builder.addCase(signup.rejected, (state, action) => {
            console.error('AUTH - SIGN UP ERROR:', action.error?.message || '');
            state.registerError = action.payload as SerializedError;
        });
        builder.addCase(login.fulfilled, (state, action) => {
            const loggedUser = action.payload;
            state.loginError = undefined;
            state.id = loggedUser.id;
            state.accessToken = loggedUser.accessToken;
            state.refreshToken = loggedUser.refreshToken;
            state.agendaId = loggedUser.agendaId;
            state.agendaUuid = loggedUser.agendaUuid;
            sessionStorage.setItem('id', state.id ? String(state.id) : '');
            sessionStorage.setItem('accessToken', state.accessToken || '');
            sessionStorage.setItem('refreshToken', state.refreshToken || '');
            sessionStorage.setItem('agendaId', String(state.agendaId) || '');
            sessionStorage.setItem('agendaUuid', String(state.agendaUuid) || '');
            console.log(`AUTH - LOGGED AS ${action?.payload?.username || ''}`);
        });
        builder.addCase(login.rejected, (state, action) => {
            delete state.accessToken;
            delete state.refreshToken;
            delete state.agendaId;
            delete state.agendaUuid;
            sessionStorage.removeItem('id');
            sessionStorage.removeItem('accessToken');
            sessionStorage.removeItem('refreshToken');
            sessionStorage.removeItem('agendaId');
            sessionStorage.removeItem('agendaUuid');
            console.error('AUTH - LOGIN ERROR:', action || '');
            action.error = action.payload as SerializedError;
            state.loginError = action.error;
        });
    }
});

export type AuthStateType = ReturnType<typeof auth.reducer>;

export default auth.reducer;

export const { logout } = auth.actions;
